import { Toaster } from "@jambushrushti/components/ui/toaster";
import { Toaster as LibraryToaster } from "@libraryadmin/components/ui/toaster";
import { Toaster as Sonner } from "@jambushrushti/components/ui/sonner";
import { TooltipProvider } from "@jambushrushti/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AdminAuthProvider } from "@jambushrushti/contexts/AdminAuthContext";
import { ProtectedLMSRoute } from "@jambushrushti/components/lms/ProtectedLMSRoute";
import { ProtectedAdminRoute } from "@jambushrushti/components/admin/ProtectedAdminRoute";
import { useGlobalScrollToTop } from "@jambushrushti/hooks/useGlobalScrollToTop";

// Public website (new design). Home loads eagerly; inner pages on demand so the
// homepage does not ship the PDF reader and every page's code.
import { SiteLayout } from "@jambushrushti/site/SiteLayout";
import { SHOW_RESEARCH_TOOLS } from "@jambushrushti/site/data/site";
import Home from "@jambushrushti/site/pages/Home";
const About = lazy(() => import("@jambushrushti/site/pages/About"));
const Gurudev = lazy(() => import("@jambushrushti/site/pages/about/Gurudev"));
const Parivar = lazy(() => import("@jambushrushti/site/pages/about/Parivar"));
const ResearchCentre = lazy(
  () => import("@jambushrushti/site/pages/about/ResearchCentre")
);
const Shantigram = lazy(
  () => import("@jambushrushti/site/pages/about/Shantigram")
);
const Guruvani = lazy(
  () => import("@jambushrushti/site/pages/library/Guruvani")
);
const LibraryCatalog = lazy(
  () => import("@jambushrushti/site/pages/library/LibraryCatalog")
);
const BooksManuscripts = lazy(
  () => import("@jambushrushti/site/pages/library/BooksManuscripts")
);
const LibraryItem = lazy(
  () => import("@jambushrushti/site/pages/library/LibraryItem")
);
const EMagazine = lazy(
  () => import("@jambushrushti/site/pages/library/EMagazine")
);
const Gallery = lazy(
  () => import("@jambushrushti/site/pages/community/Gallery")
);
const LiveTelecast = lazy(
  () => import("@jambushrushti/site/pages/community/LiveTelecast")
);
const Contact = lazy(() => import("@jambushrushti/site/pages/Contact"));
const Events = lazy(() => import("@jambushrushti/site/pages/community/Events"));
const EventDetail = lazy(() =>
  import("@jambushrushti/site/pages/community/Events").then(m => ({
    default: m.EventDetail,
  }))
);
const Blog = lazy(() => import("@jambushrushti/site/pages/community/Blog"));
const BlogPostPage = lazy(() =>
  import("@jambushrushti/site/pages/community/Blog").then(m => ({
    default: m.BlogPostPage,
  }))
);
const Research = lazy(() => import("@jambushrushti/site/pages/Research"));
const SearchPage = lazy(() => import("@jambushrushti/site/pages/Search"));
const NotFound = lazy(() =>
  import("@jambushrushti/site/pages/Search").then(m => ({
    default: m.NotFound,
  }))
);

// Public pages without a counterpart in the new design. They render inside the
// new site shell through <Layout>.
import GuruvaniDetails from "./pages/GuruvaniDetails";
import Auth from "./pages/Auth";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import News from "./pages/community/News";

/** Old URL with an :id → its page in the new site structure. */
function RedirectWithId({ to }: { to: (id: string) => string }) {
  const { id = "" } = useParams();
  return <Navigate to={to(id)} replace />;
}

// Research Modules
import SodhSanchay from "./pages/research/SodhSanchay";
import SodhSandarbh from "./pages/research/SodhSandarbh";
import Shabdasangraha from "./pages/research/Shabdasangraha";
import Shastrasandarbha from "./pages/research/Shastrasandarbha";
import IndologyResearch from "./pages/research/IndologyResearch";

// LMS Pages
import LMSLogin from "./pages/lms/LMSLogin";
import LMSDashboard from "./pages/lms/LMSDashboard";
import BookEntry from "./pages/lms/BookEntry";
import BookIssue from "./pages/lms/BookIssue";
import BookReceive from "./pages/lms/BookReceive";
import Manuscripts from "./pages/lms/Manuscripts";
import Members from "./pages/lms/Members";
import Approvals from "./pages/lms/Approvals";
import Reports from "./pages/lms/Reports";
import TicketManagement from "./pages/lms/TicketManagement";
import LMSSettings from "./pages/lms/LMSSettings";

// Admin Pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import UsersManagement from "./pages/admin/UsersManagement";
import GuruvaniManagement from "./pages/admin/GuruvaniManagement";
import {
  ResearchPage,
  MuseumPage,
  AIPage,
  AccessRequestsPage,
  LogsPage,
  SettingsPage,
} from "./pages/admin/AdminModules";
import CMSManagement from "./pages/admin/CMSManagement";
import BooksManagement from "./pages/admin/BooksManagement";
import ArticlesManagement from "./pages/admin/ArticlesManagement";
import BlogManagement from "./pages/admin/BlogManagement";
import EventsManagement from "./pages/admin/EventsManagement";
import GalleryManagement from "./pages/admin/GalleryManagement";
import EmagazineManagement from "./pages/admin/EmagazineManagement";
import SocialMediaManagement from "./pages/admin/SocialMediaManagement";
import FeedbackManagement from "./pages/admin/FeedbackManagement";
import ContactUsManagement from "./pages/admin/ContactUsManagement";
import LiveTelecastManagement from "./pages/admin/LiveTelecastManagement";
import LibraryDashboard from "@libraryadmin/pages/Index";
import LibraryLogin from "@libraryadmin/pages/Login";
import LibraryBookEntry from "@libraryadmin/pages/BookEntry";
import LibraryBookIssue from "@libraryadmin/pages/BookIssue";
import LibraryBookReceive from "@libraryadmin/pages/BookReceive";
import LibraryStorage from "@libraryadmin/pages/Storage";
import LibraryManuscripts from "@libraryadmin/pages/Manuscripts";
import LibraryMembers from "@libraryadmin/pages/Members";
import LibraryFineManagement from "@libraryadmin/pages/FineManagement";
import LibraryApprovals from "@libraryadmin/pages/Approvals";
import LibraryReports from "@libraryadmin/pages/Reports";
import LibraryTickets from "@libraryadmin/pages/Tickets";
import LibrarySettings from "@libraryadmin/pages/Settings";
import MemberDetails from "@libraryadmin/pages/MemberDetails";
import { ProtectedLibraryRoute } from "@libraryadmin/components/auth/ProtectedLibraryRoute";
import "@libraryadmin/index.css";

// Learning Portal Pages
import LearningCourses from "./pages/learning/Courses";
import LearningCourseDetail from "./pages/learning/CourseDetail";
import LearningLogin from "./pages/learning/LearningLogin";
import LMSAdminDashboard from "./pages/learning/LMSAdminDashboard";
import LearningDashboard from "./pages/learning/LMSDashboard";
import CoursesManage from "./pages/learning/CoursesManage";
import LearningLecturesManage from "./pages/learning/LecturesManage";
import StudentsManage from "./pages/learning/StudentsManage";
import AttendanceManage from "./pages/learning/AttendanceManage";
import ScheduleManage from "./pages/learning/ScheduleManage";
import MaterialsManage from "./pages/learning/MaterialsManage";
import ReportsManage from "./pages/learning/ReportsManage";
import LearningSettings from "./pages/learning/LMSSettings";
import ProgramsManage from "./pages/learning/ProgramsManage";
import BatchesManage from "./pages/learning/BatchesManage";
import QuizzesManage from "./pages/learning/QuizzesManage";
import AssignmentsManage from "./pages/learning/AssignmentsManage";
import StudentDashboard from "./pages/learning/StudentDashboard";
import MyCertificates from "./pages/learning/MyCertificates";
import EnrolledCourses from "./pages/learning/EnrolledCourses";
import MySchedule from "./pages/learning/MySchedule";
import MyAssignments from "./pages/learning/MyAssignments";
import MyQuizzes from "./pages/learning/MyQuizzes";
import MyMaterials from "./pages/learning/MyMaterials";
import LMSCoursesManagement from "./pages/admin/LMSCoursesManagement";
import LecturesManagement from "./pages/admin/LecturesManagement";

// Ticket Management Pages
import TicketDashboard from "./pages/tickets/TicketDashboard";
import CreateTicket from "./pages/tickets/CreateTicket";
import TicketList from "./pages/tickets/TicketList";
import TicketDetail from "./pages/tickets/TicketDetail";
import TicketCategories from "./pages/tickets/TicketCategories";
import TicketReports from "./pages/tickets/TicketReports";
import SLASettings from "./pages/tickets/SLASettings";
import MyTickets from "./pages/tickets/MyTickets";
import { ProtectedTicketRoute } from "@jambushrushti/components/tickets/ProtectedTicketRoute";
import { ProtectedLearningRoute } from "@jambushrushti/components/learning/ProtectedLearningRoute";
import { ProtectedStudentRoute } from "@jambushrushti/components/learning/ProtectedStudentRoute";

// Reuse fetched data for 30s when moving between pages, retry a failed request
// once instead of three times with backoff, and don't refetch on tab focus.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

// Wrapper component to use the hook
function AppContent() {
  useGlobalScrollToTop();

  return (
    <Routes>
      {/* Public website (new design) */}
      <Route element={<SiteLayout />}>
        <Route path="/" element={<Home />} />

        {/* About */}
        <Route path="/about" element={<About />} />
        <Route path="/about/gurudev" element={<Gurudev />} />
        <Route path="/about/parivar" element={<Parivar />} />
        <Route path="/about/gyan-kendra" element={<ResearchCentre />} />
        <Route path="/about/adani-shantigram" element={<Shantigram />} />

        {/* Guruvani */}
        <Route path="/guruvani" element={<Guruvani />} />

        {/* Research overview (the tool pages are below). Hidden for now: the
            URL falls through to the 404 page while SHOW_RESEARCH_TOOLS is false. */}
        {SHOW_RESEARCH_TOOLS && (
          <Route path="/research" element={<Research />} />
        )}

        {/* Library */}
        <Route path="/library" element={<LibraryCatalog />} />
        <Route path="/library/books" element={<BooksManuscripts />} />
        <Route path="/library/item/:key" element={<LibraryItem />} />
        <Route path="/emagazine" element={<EMagazine />} />

        {/* Gallery */}
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/live-telecast" element={<LiveTelecast />} />

        {/* Community */}
        <Route path="/community/events" element={<Events />} />
        <Route path="/community/events/:id" element={<EventDetail />} />
        <Route path="/community/blog" element={<Blog />} />
        <Route
          path="/community/blog/:id"
          element={<BlogPostPage kind="blog" />}
        />
        <Route
          path="/community/articles/:id"
          element={<BlogPostPage kind="article" />}
        />

        {/* Contact & search */}
        <Route path="/contact" element={<Contact />} />
        <Route path="/search" element={<SearchPage />} />

        <Route path="*" element={<NotFound />} />
      </Route>

      {/* Old URLs → their pages in the new site structure */}
      <Route
        path="/library/:id"
        element={<RedirectWithId to={id => `/library/item/book-${id}`} />}
      />
      <Route path="/books" element={<Navigate to="/library/books" replace />} />
      <Route
        path="/books/:id"
        element={<RedirectWithId to={id => `/library/item/book-${id}`} />}
      />
      <Route
        path="/articles"
        element={<Navigate to="/community/blog" replace />}
      />
      <Route
        path="/articles/:id"
        element={<RedirectWithId to={id => `/community/articles/${id}`} />}
      />
      <Route
        path="/community"
        element={<Navigate to="/community/events" replace />}
      />
      <Route
        path="/feedback"
        element={<Navigate to="/contact?form=feedback#ct-form" replace />}
      />

      {/* Public pages not in the new design yet (rendered in the new shell via <Layout>) */}
      <Route path="/guruvani/:id" element={<GuruvaniDetails />} />
      {/* Research tools: hidden until the client approves them (SHOW_RESEARCH_TOOLS) */}
      {SHOW_RESEARCH_TOOLS && (
        <>
          <Route path="/research/sodhsanchay" element={<SodhSanchay />} />
          <Route path="/research/sodhsandarbh" element={<SodhSandarbh />} />
          <Route path="/research/shabdasangraha" element={<Shabdasangraha />} />
          <Route
            path="/research/shastrasandarbha"
            element={<Shastrasandarbha />}
          />
        </>
      )}
      <Route path="/research/indology" element={<IndologyResearch />} />
      <Route path="/community/news" element={<News />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />

      {/* Jambushrushti Library Administration */}
      <Route path="/vault-library-admin" element={<LibraryLogin />} />
      <Route element={<ProtectedLibraryRoute />}>
        <Route
          path="/vault-library-admin/dashboard"
          element={<LibraryDashboard />}
        />
        <Route
          path="/vault-library-admin/book-entry"
          element={<LibraryBookEntry />}
        />
        <Route
          path="/vault-library-admin/book-issue"
          element={<LibraryBookIssue />}
        />
        <Route
          path="/vault-library-admin/book-issue/:issueId"
          element={<LibraryBookIssue />}
        />
        <Route
          path="/vault-library-admin/book-receive"
          element={<LibraryBookReceive />}
        />
        <Route
          path="/vault-library-admin/storage"
          element={<LibraryStorage />}
        />
        <Route
          path="/vault-library-admin/manuscripts"
          element={<LibraryManuscripts />}
        />
        <Route
          path="/vault-library-admin/members"
          element={<LibraryMembers />}
        />
        <Route
          path="/vault-library-admin/members/:id"
          element={<MemberDetails />}
        />
        <Route
          path="/vault-library-admin/fine-management"
          element={<LibraryFineManagement />}
        />
        <Route
          path="/vault-library-admin/fines"
          element={<LibraryFineManagement />}
        />
        <Route
          path="/vault-library-admin/approvals"
          element={<LibraryApprovals />}
        />
        <Route
          path="/vault-library-admin/reports"
          element={<LibraryReports />}
        />
        <Route
          path="/vault-library-admin/tickets"
          element={<LibraryTickets />}
        />
        <Route
          path="/vault-library-admin/settings"
          element={<LibrarySettings />}
        />
        <Route
          path="/vault-library-admin/users"
          element={<UsersManagement portal="library" />}
        />
      </Route>

      {/* LMS Routes */}
      <Route path="/lms" element={<LMSLogin />} />
      <Route
        path="/lms/dashboard"
        element={
          <ProtectedLMSRoute>
            <LMSDashboard />
          </ProtectedLMSRoute>
        }
      />
      <Route
        path="/lms/book-entry"
        element={
          <ProtectedLMSRoute>
            <BookEntry />
          </ProtectedLMSRoute>
        }
      />
      <Route
        path="/lms/book-issue"
        element={
          <ProtectedLMSRoute>
            <BookIssue />
          </ProtectedLMSRoute>
        }
      />
      <Route
        path="/lms/book-receive"
        element={
          <ProtectedLMSRoute>
            <BookReceive />
          </ProtectedLMSRoute>
        }
      />
      <Route
        path="/lms/manuscripts"
        element={
          <ProtectedLMSRoute>
            <Manuscripts />
          </ProtectedLMSRoute>
        }
      />
      <Route
        path="/lms/members"
        element={
          <ProtectedLMSRoute>
            <Members />
          </ProtectedLMSRoute>
        }
      />
      <Route
        path="/lms/approvals"
        element={
          <ProtectedLMSRoute>
            <Approvals />
          </ProtectedLMSRoute>
        }
      />
      <Route
        path="/lms/reports"
        element={
          <ProtectedLMSRoute>
            <Reports />
          </ProtectedLMSRoute>
        }
      />
      <Route
        path="/lms/tickets"
        element={
          <ProtectedLMSRoute>
            <TicketManagement />
          </ProtectedLMSRoute>
        }
      />
      <Route
        path="/lms/settings"
        element={
          <ProtectedLMSRoute>
            <LMSSettings />
          </ProtectedLMSRoute>
        }
      />

      {/* Learning Portal Routes */}
      <Route path="/learning" element={<LearningLogin />} />
      <Route path="/learning/login" element={<LearningLogin />} />
      <Route path="/learning/courses" element={<LearningCourses />} />
      <Route
        path="/learning/courses/:courseId"
        element={
          <ProtectedStudentRoute>
            <LearningCourseDetail />
          </ProtectedStudentRoute>
        }
      />
      <Route
        path="/learning/student-dashboard"
        element={
          <ProtectedStudentRoute>
            <StudentDashboard />
          </ProtectedStudentRoute>
        }
      />
      <Route
        path="/learning/admin-dashboard"
        element={
          <ProtectedLearningRoute>
            <LMSAdminDashboard />
          </ProtectedLearningRoute>
        }
      />
      <Route
        path="/learning/dashboard"
        element={
          <ProtectedLearningRoute>
            <LearningDashboard />
          </ProtectedLearningRoute>
        }
      />
      <Route
        path="/learning/manage-courses"
        element={
          <ProtectedLearningRoute>
            <CoursesManage />
          </ProtectedLearningRoute>
        }
      />
      <Route
        path="/learning/manage-lectures"
        element={
          <ProtectedLearningRoute>
            <LearningLecturesManage />
          </ProtectedLearningRoute>
        }
      />
      <Route
        path="/learning/students"
        element={
          <ProtectedLearningRoute>
            <StudentsManage />
          </ProtectedLearningRoute>
        }
      />
      <Route
        path="/learning/attendance"
        element={
          <ProtectedLearningRoute>
            <AttendanceManage />
          </ProtectedLearningRoute>
        }
      />
      <Route
        path="/learning/schedule"
        element={
          <ProtectedLearningRoute>
            <ScheduleManage />
          </ProtectedLearningRoute>
        }
      />
      <Route
        path="/learning/materials"
        element={
          <ProtectedLearningRoute>
            <MaterialsManage />
          </ProtectedLearningRoute>
        }
      />
      <Route
        path="/learning/reports"
        element={
          <ProtectedLearningRoute>
            <ReportsManage />
          </ProtectedLearningRoute>
        }
      />
      <Route
        path="/learning/settings"
        element={
          <ProtectedLearningRoute>
            <LearningSettings />
          </ProtectedLearningRoute>
        }
      />
      <Route
        path="/learning/programs"
        element={
          <ProtectedLearningRoute>
            <ProgramsManage />
          </ProtectedLearningRoute>
        }
      />
      <Route
        path="/learning/batches"
        element={
          <ProtectedLearningRoute>
            <BatchesManage />
          </ProtectedLearningRoute>
        }
      />
      <Route
        path="/learning/quizzes"
        element={
          <ProtectedLearningRoute>
            <QuizzesManage />
          </ProtectedLearningRoute>
        }
      />
      <Route
        path="/learning/assignments"
        element={
          <ProtectedLearningRoute>
            <AssignmentsManage />
          </ProtectedLearningRoute>
        }
      />
      <Route
        path="/learning/certificates"
        element={
          <ProtectedStudentRoute>
            <MyCertificates />
          </ProtectedStudentRoute>
        }
      />
      <Route
        path="/learning/my-courses"
        element={
          <ProtectedStudentRoute>
            <EnrolledCourses />
          </ProtectedStudentRoute>
        }
      />
      <Route
        path="/learning/my-schedule"
        element={
          <ProtectedStudentRoute>
            <MySchedule />
          </ProtectedStudentRoute>
        }
      />
      <Route
        path="/learning/my-assignments"
        element={
          <ProtectedStudentRoute>
            <MyAssignments />
          </ProtectedStudentRoute>
        }
      />
      <Route
        path="/learning/my-quizzes"
        element={
          <ProtectedStudentRoute>
            <MyQuizzes />
          </ProtectedStudentRoute>
        }
      />
      <Route
        path="/learning/my-materials"
        element={
          <ProtectedStudentRoute>
            <MyMaterials />
          </ProtectedStudentRoute>
        }
      />
      {/* Admin Routes */}
      <Route path="/vault-cms-admin" element={<AdminLogin />} />
      <Route
        path="/vault-cms-admin/dashboard"
        element={
          <ProtectedAdminRoute>
            <AdminDashboard />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/users"
        element={
          <ProtectedAdminRoute>
            <UsersManagement />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/guruvani"
        element={
          <ProtectedAdminRoute>
            <GuruvaniManagement />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/cms"
        element={
          <ProtectedAdminRoute>
            <CMSManagement />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/gallery"
        element={
          <ProtectedAdminRoute>
            <GalleryManagement />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/books"
        element={
          <ProtectedAdminRoute>
            <BooksManagement />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/articles"
        element={
          <ProtectedAdminRoute>
            <ArticlesManagement />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/blog"
        element={
          <ProtectedAdminRoute>
            <BlogManagement />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/events"
        element={
          <ProtectedAdminRoute>
            <EventsManagement />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/emagazine"
        element={
          <ProtectedAdminRoute>
            <EmagazineManagement />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/social-media"
        element={
          <ProtectedAdminRoute>
            <SocialMediaManagement />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/feedback"
        element={
          <ProtectedAdminRoute>
            <FeedbackManagement />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/contact-us"
        element={
          <ProtectedAdminRoute>
            <ContactUsManagement />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/live-telecast"
        element={
          <ProtectedAdminRoute>
            <LiveTelecastManagement />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/research"
        element={
          <ProtectedAdminRoute>
            <ResearchPage />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/museum"
        element={
          <ProtectedAdminRoute>
            <MuseumPage />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/ai"
        element={
          <ProtectedAdminRoute>
            <AIPage />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/access-requests"
        element={
          <ProtectedAdminRoute>
            <AccessRequestsPage />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/logs"
        element={
          <ProtectedAdminRoute>
            <LogsPage />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/settings"
        element={
          <ProtectedAdminRoute>
            <SettingsPage />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/learning-courses"
        element={
          <ProtectedAdminRoute>
            <LMSCoursesManagement />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/vault-cms-admin/lectures"
        element={
          <ProtectedAdminRoute>
            <LecturesManagement />
          </ProtectedAdminRoute>
        }
      />

      {/* Ticket Management Routes */}
      <Route
        path="/tickets"
        element={
          <ProtectedTicketRoute>
            <TicketDashboard />
          </ProtectedTicketRoute>
        }
      />
      <Route
        path="/tickets/dashboard"
        element={
          <ProtectedTicketRoute>
            <TicketDashboard />
          </ProtectedTicketRoute>
        }
      />
      <Route
        path="/tickets/create"
        element={
          <ProtectedTicketRoute>
            <CreateTicket />
          </ProtectedTicketRoute>
        }
      />
      <Route
        path="/tickets/list"
        element={
          <ProtectedTicketRoute>
            <TicketList />
          </ProtectedTicketRoute>
        }
      />
      <Route
        path="/tickets/my-tickets"
        element={
          <ProtectedTicketRoute>
            <MyTickets />
          </ProtectedTicketRoute>
        }
      />
      <Route
        path="/tickets/categories"
        element={
          <ProtectedTicketRoute>
            <TicketCategories />
          </ProtectedTicketRoute>
        }
      />
      <Route
        path="/tickets/reports"
        element={
          <ProtectedTicketRoute>
            <TicketReports />
          </ProtectedTicketRoute>
        }
      />
      <Route
        path="/tickets/sla-settings"
        element={
          <ProtectedTicketRoute>
            <SLASettings />
          </ProtectedTicketRoute>
        }
      />
      <Route
        path="/tickets/:id"
        element={
          <ProtectedTicketRoute>
            <TicketDetail />
          </ProtectedTicketRoute>
        }
      />
    </Routes>
  );
}

type AppProps = {
  basename?: string;
};

const App = ({ basename }: AppProps) => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AdminAuthProvider>
          <Toaster />
          <div className="library-admin-module">
            <LibraryToaster />
          </div>
          <Sonner />
          <BrowserRouter basename={basename}>
            <AppContent />
          </BrowserRouter>
        </AdminAuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
